import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcGridXlsx from '@mescius/wijmo.grid.xlsx';
import {DataService, OrientDBCollectionView} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {savePivotXlsx} from './pivot-xlsx';

/**
 * kobe-ac の AC_Item グリッド画面 (履歴/入力/精算) の共通部。
 * 旧 AuctionGridController / AuctionSummaryController が共有していた
 * CollectionView・DataMap 群・エクスポートをまとめる。
 */
export abstract class KobeacItemGridBase {
    items: OrientDBCollectionView;
    auctions: any[] = [];
    current: any = null;
    tax = 10;

    auctionMap: wjcGrid.DataMap;
    bidderIdMap: wjcGrid.DataMap;
    bidderNameMap: wjcGrid.DataMap;
    supplierIdMap: wjcGrid.DataMap;
    supplierNameMap: wjcGrid.DataMap;
    categoryMap: wjcGrid.DataMap;

    protected constructor(protected dataService: DataService, public kobeac: KobeacService) {
        this.items = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE,
            'SELECT FROM AC_Item', 'AC_Item', '', 0, 5000, true, true, false);
        this.items.url = ActivePromotionConfig.SERVER_URL;
        this.items.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.items.fetchPlan = '*:0';
        this.items.itemsSourceFunction = (rows: any[]) => this.kobeac.resolveCustomers(rows);
        // 伝票と照合しやすいよう一覧は ID (商品管理番号) 昇順 (サーバ側 ORDER BY、旧 kobe-ac.app と同じ)。列見出しで並べ替え可能
        this.items.sortDescriptions.push(new wjcCore.SortDescription('ID', true));
    }

    /** マスタ読込後に呼ぶ */
    protected initMaps(): void {
        this.auctions = this.kobeac.auctions;
        this.auctionMap = this.kobeac.buildDataMap(this.kobeac.auctions, (a) => this.kobeac.formatOrientDate(a['日付']));
        this.bidderIdMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['買主']), (c) => String(c.ID));
        this.bidderNameMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['買主']), (c) => c['名称']);
        this.supplierIdMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['売主']), (c) => String(c.ID));
        this.supplierNameMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['売主']), (c) => c['名称']);
        this.categoryMap = this.kobeac.buildDataMap(this.kobeac.categories, (c) => c['名称']);
    }

    auctionDateFormatter = (index: number, content: string) => {
        return this.kobeac.formatOrientDate(content);
    };

    onAuctionChanged(): void {
        if (wjcCore.isObject(this.current)) {
            this.items.filterSpecial = '`オークション` = ' + this.current['@rid'];
            this.items.filterOnServer = true;
            if (wjcCore.isNumber(this.current['消費税'])) this.tax = this.current['消費税'];
        }
    }

    itemFormatter = (panel: wjcGrid.GridPanel, r: number, c: number, cell: HTMLElement) => {
        if (panel.cellType == wjcGrid.CellType.Cell && panel.rows[r].dataItem != null) {
            // 選択中の見た目は style.scss の .ap-readonly-tint で選択色に戻す (選択変更では itemFormatter が再実行されない)
            if (panel.grid.isReadOnly || panel.rows[r].isReadOnly || panel.columns[c].isReadOnly) {
                // 読み取り専用は背景色だけで示し、文字は通常の黒のまま (代表指示: 灰色は読みにくい)
                cell.style.backgroundColor = '#ffffe0';
            } else {
                cell.style.backgroundColor = '';
            }
        }
    };

    isEdited(row: wjcGrid.Row): boolean {
        if (row.dataItem == null) return false;
        for (const item of this.items.itemsEdited) {
            if (item['@rid'] !== undefined && item['@rid'] == row.dataItem['@rid']) return true;
        }
        return false;
    }

    isNew(row: wjcGrid.Row): boolean {
        return row.dataItem != null && row.dataItem['@rid'] === undefined;
    }

    exportGrid(grid: wjcGrid.FlexGrid, includeRowHeaders = false): void {
        if (grid == null) return;
        const filename = 'export(' + wjcCore.Globalize.formatDate(new Date(), 'yyyyMMddHHmmss') + ').xlsx';
        if (includeRowHeaders) {
            savePivotXlsx(grid, filename, true);
            return;
        }
        // 同期 save() は JSZip 2.5 専用。同梱の JSZip 3 では assert で例外になり何も出力されない
        wjcGridXlsx.FlexGridXlsxConverter.saveAsync(grid, {includeColumnHeaders: true, includeCellStyles: false}, filename);
    }
}
